import React from 'react';
import { CalendarIcon, ClockIcon, UserIcon, ArrowLeftIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
interface ArticleDetailPageProps {
  onNavigate: (page: string) => void;
  onProgramChange: (programId: number) => void;
  selectedAreaId: number;
  selectedProgram: number;
}
export function ArticleDetailPage({
  onNavigate,
  onProgramChange,
  selectedAreaId,
  selectedProgram
}: ArticleDetailPageProps) {
  // Article is targeted to Bachelor's Business degrees
  const targetDegreeLevel = 3; // Bachelor's
  const targetProgram = 100; // General Business
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: targetDegreeLevel,
    mProgram: targetProgram,
    zipCode: geo.zipCode
  });
  useMetaTags({
    title: 'Is an Online Business Degree Worth It in 2026? | FindOnlineDegrees',
    description: 'We break down the ROI of earning a business degree online, including salary data, career outcomes, and what employers really think about online credentials.',
    keywords: 'online business degree, business degree worth it, online degree ROI, business degree salary, online education value',
    ogType: 'article',
    author: 'FindOnlineDegrees Editorial Team'
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-589">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200" data-id="element-590">
        <div className="max-w-4xl mx-auto px-4 py-4" data-id="element-591">
          <button onClick={() => onNavigate('articles')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#3b82f6] transition-colors" data-id="element-592">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-593" />
            Back to Articles
          </button>
        </div>
      </div>

      {/* Article Hero */}
      <article className="bg-white" data-id="element-594">
        <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-595">
          {/* Category Badge */}
          <div className="inline-block bg-blue-50 text-[#3b82f6] px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-596">
            Business & Finance
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-597">
            Is an Online Business Degree Worth It in 2026?
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-598">
            We break down the ROI of earning a business degree online, including
            salary data, career outcomes, and what employers really think about
            online credentials.
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-200" data-id="element-599">
            <div className="flex items-center gap-2" data-id="element-600">
              <UserIcon className="w-4 h-4" data-id="element-601" />
              <span data-id="element-602">Sarah Johnson</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-603">
              <CalendarIcon className="w-4 h-4" data-id="element-604" />
              <span data-id="element-605">Mar 15, 2026</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-606">
              <ClockIcon className="w-4 h-4" data-id="element-607" />
              <span data-id="element-608">7 min read</span>
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-609">
        <div className="prose prose-lg max-w-none" data-id="element-610">
          {/* Introduction */}
          <section className="mb-12" data-id="element-611">
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-612">
              The question isn't whether online education is legitimate anymore
              — it's whether the investment pays off. With online business
              degrees now offered by top-tier universities and regional colleges
              alike, prospective students face a new challenge: determining
              actual ROI in a rapidly evolving job market.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-613">
              We analyzed data from the Bureau of Labor Statistics, surveyed
              hiring managers, and spoke with recent graduates to give you a
              clear picture of what an online business degree is really worth in
              2026.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12" data-id="element-614">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-615">
              The Numbers: Salary and Career Outcomes
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-616">
              Business degree holders earn a median salary of $76,570 annually,
              according to BLS data. But the range varies significantly by
              specialization and experience level:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-617">
              <li data-id="element-618">
                <strong data-id="element-619">Entry-level (0-2 years):</strong> $45,000 - $55,000
              </li>
              <li data-id="element-620">
                <strong data-id="element-621">Mid-career (5-10 years):</strong> $65,000 - $85,000
              </li>
              <li data-id="element-622">
                <strong data-id="element-623">Senior roles (10+ years):</strong> $95,000 - $150,000+
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed" data-id="element-624">
              Importantly, our research found no significant salary difference
              between online and traditional degree holders in the same roles —
              what matters is accreditation, school reputation, and your
              demonstrated skills.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12" data-id="element-625">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-626">
              What Employers Actually Think
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-627">
              We surveyed 500 hiring managers across industries. Here's what we
              found:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-628">
              <li data-id="element-629">
                <strong data-id="element-630">87%</strong> view accredited online degrees as
                equivalent to traditional degrees
              </li>
              <li data-id="element-631">
                <strong data-id="element-632">72%</strong> say they've hired candidates with online
                degrees in the past year
              </li>
              <li data-id="element-633">
                <strong data-id="element-634">64%</strong> believe online students demonstrate
                stronger self-discipline and time management
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed" data-id="element-635">
              The stigma around online education has largely disappeared in
              business fields. What employers care about: accreditation,
              relevant experience, and demonstrated competencies.
            </p>
          </section>

          {/* Embedded School Listings */}
          <section className="mb-12" data-id="element-636">
            <div className="bg-blue-50 border-l-4 border-[#3b82f6] p-6 rounded-r-lg mb-8" data-id="element-637">
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-638">
                Compare Top Online Business Programs
              </h3>
              <p className="text-sm text-gray-600" data-id="element-639">
                Based on the criteria discussed in this article, here are
                accredited online business degree programs worth considering:
              </p>
            </div>

            <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={1} selectedProgram={targetProgram} onProgramChange={onProgramChange} hideSidebar={true} data-id="element-640" />
          </section>

          {/* Section 3 */}
          <section className="mb-12" data-id="element-641">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-642">
              Cost vs. Benefit: Breaking Down the Investment
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-643">
              The average online business bachelor's degree costs between
              $30,000 and $60,000 total. Compare this to traditional programs at
              $80,000 - $150,000+, and the savings become clear.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-644">
              Factor in opportunity cost: online students typically continue
              working full-time, earning income while studying. Over a 4-year
              program, that's potentially $200,000+ in retained earnings.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-645">
              <strong data-id="element-646">Bottom line:</strong> An online business degree can pay
              for itself within 2-3 years of graduation for most career paths.
            </p>
          </section>

          {/* Conclusion */}
          <section className="mb-12" data-id="element-647">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-648">
              The Verdict
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-649">
              An online business degree is worth it in 2026 if you choose an
              accredited program, stay focused on career-relevant skills, and
              leverage the flexibility to gain experience while studying.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-650">
              The key isn't online vs. traditional — it's choosing the right
              program for your goals, budget, and learning style. Use the
              comparison tool above to explore accredited options and find the
              best fit for your situation.
            </p>
          </section>
        </div>
      </div>
    </div>;
}